import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useBookDemo } from "../../context/BookDemoContext";

const BASE_URL = "https://r4lsolutions.com/backend/api/provider";

// Endpoints and payloads mirror the provider guide rendered on the public
// documentation page (/documentation, src/i18n/docs). They are intentionally
// kept out of the locale files — only the prose around them is translated.
const capabilities = [
  { method: "POST", endpoint: "/sim-requests" },
  { method: "GET", endpoint: "/esims" },
  { method: "PATCH", endpoint: "/esims/{iccid}/assign" },
  { method: "POST", endpoint: "/esims/sync" },
];

const exampleCalls = [
  {
    method: "POST",
    path: "/sim-requests",
    request: `POST ${BASE_URL}/sim-requests
Content-Type: application/json

{
  "quantity": 5,
  "note": "Need for Q3 campaign"
}`,
    response: `{
  "id": "req_9f21c4",
  "quantity": 5,
  "note": "Need for Q3 campaign",
  "status": "pending",
  "created_at": "2026-06-17T08:00:00.000Z"
}`,
  },
  {
    method: "PATCH",
    path: "/esims/{iccid}/assign",
    request: `PATCH ${BASE_URL}/esims/8910300000041170580/assign
Content-Type: application/json

{
  "customer_id": "cust_abc123"
}`,
    response: `{
  "iccid": "8910300000041170580",
  "activation_code": "LPA:1$consumer.e-sim.global$TN2025...",
  "status": "pre_service",
  "customer_id": "cust_abc123",
  "activated_at": null
}`,
  },
  {
    method: "POST",
    path: "/esims/sync",
    request: `POST ${BASE_URL}/esims/sync`,
    response: `{
  "total_checked": 5,
  "total_updated": 2,
  "updated": [
    {
      "iccid": "8910300000041170580",
      "old_status": "pre_service",
      "new_status": "in_service",
      "activated_at": "2026-06-17T08:00:00.000Z"
    }
  ],
  "failed": []
}`,
  },
];

// Order matches developers.webhooks.statuses in the locale files.
const statusCodes = [
  "waiting_for_assignment",
  "pre_service",
  "in_service",
  "terminated",
];

function CodeBlock({ label, code }) {
  return (
    <div className="developers-code">
      <p className="developers-code-label">{label}</p>
      <pre className="developers-pre">
        <code>{code}</code>
      </pre>
    </div>
  );
}

function MethodBadge({ method }) {
  return (
    <span
      className={`developers-method developers-method--${method.toLowerCase()}`}
    >
      {method}
    </span>
  );
}

function Developers() {
  const { t } = useTranslation();
  const { openBookDemo } = useBookDemo();

  const capabilityItems = t("developers.capabilities.items", {
    returnObjects: true,
  });
  const exampleItems = t("developers.examples.items", { returnObjects: true });
  const webhookSteps = t("developers.webhooks.steps", { returnObjects: true });
  const statusLabels = t("developers.webhooks.statuses", {
    returnObjects: true,
  });
  const sandboxSteps = t("developers.sandbox.steps", { returnObjects: true });

  return (
    <section className="developers-page py-50">
      <div className="container">
        <header className="developers-hero">
          <p className="developers-eyebrow">{t("developers.eyebrow")}</p>
          <h1 className="developers-hero-title">{t("developers.title")}</h1>
          <p className="developers-hero-desc">{t("developers.desc")}</p>

          <div className="developers-hero-meta">
            <p className="developers-code-label">
              {t("developers.baseUrlLabel")}
            </p>
            <code className="developers-hero-baseurl">{BASE_URL}</code>
            <p className="developers-note">{t("developers.authNote")}</p>
          </div>

          <div className="developers-hero-actions">
            <button
              className="btn-cs btn-booking"
              type="button"
              onClick={openBookDemo}
            >
              {t("developers.ctaPrimary")}
            </button>
            {/* Public docs route — readable without a dashboard account. */}
            <Link className="btn-cs btn-lang developers-hero-docs" to="/documentation">
              {t("developers.ctaDocs")}
            </Link>
          </div>
        </header>

        <section className="developers-block">
          <h2 className="developers-block-title">
            {t("developers.capabilities.title")}
          </h2>
          <p className="developers-block-desc">
            {t("developers.capabilities.desc")}
          </p>

          <div className="developers-grid">
            {capabilityItems.map((item, index) => (
              <article className="developers-feature" key={item.title}>
                <MethodBadge method={capabilities[index].method} />
                <h3 className="developers-feature-title">{item.title}</h3>
                <p className="developers-feature-text">{item.text}</p>
                <code className="developers-feature-endpoint">
                  {capabilities[index].endpoint}
                </code>
              </article>
            ))}
          </div>
        </section>

        <section className="developers-block">
          <h2 className="developers-block-title">
            {t("developers.examples.title")}
          </h2>
          <p className="developers-block-desc">
            {t("developers.examples.desc")}
          </p>

          <div className="developers-examples">
            {exampleItems.map((item, index) => {
              const call = exampleCalls[index];

              return (
                <article className="developers-example" key={item.title}>
                  <div className="developers-example-head">
                    <MethodBadge method={call.method} />
                    <code className="developers-example-path">{call.path}</code>
                  </div>
                  <h3 className="developers-feature-title">{item.title}</h3>
                  <p className="developers-feature-text">{item.text}</p>

                  <div className="developers-code-grid">
                    <CodeBlock
                      label={t("developers.examples.requestLabel")}
                      code={call.request}
                    />
                    <CodeBlock
                      label={t("developers.examples.responseLabel")}
                      code={call.response}
                    />
                  </div>
                </article>
              );
            })}
          </div>
        </section>

        <section className="developers-block">
          <h2 className="developers-block-title">
            {t("developers.webhooks.title")}
          </h2>
          <p className="developers-block-desc">
            {t("developers.webhooks.desc")}
          </p>

          <div className="developers-split">
            <ol className="developers-flow">
              {webhookSteps.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>

            <div className="developers-status-card">
              <h3 className="developers-feature-title">
                {t("developers.webhooks.tableTitle")}
              </h3>
              <table className="developers-table">
                <thead>
                  <tr>
                    <th>{t("developers.webhooks.tableHead.status")}</th>
                    <th>{t("developers.webhooks.tableHead.label")}</th>
                  </tr>
                </thead>
                <tbody>
                  {statusCodes.map((code, index) => (
                    <tr key={code}>
                      <td>
                        <code>{code}</code>
                      </td>
                      <td>{statusLabels[index]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="developers-note">{t("developers.webhooks.note")}</p>
            </div>
          </div>
        </section>

        <aside className="developers-sandbox">
          <h2 className="developers-block-title">
            {t("developers.sandbox.title")}
          </h2>
          <p className="developers-block-desc">{t("developers.sandbox.desc")}</p>

          <ol className="developers-flow developers-flow--inline">
            {sandboxSteps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>

          <button
            className="btn-cs btn-booking"
            type="button"
            onClick={openBookDemo}
          >
            {t("developers.sandbox.cta")}
          </button>
        </aside>
      </div>
    </section>
  );
}

export default Developers;
